import type { OrgMemberRole, TenantOrgPermission } from '@nadoutong/contracts';
import type { SessionUser } from '../hooks/useSession.tsx';
import { API_KEYS_PERMISSIONS, hasAnyPermission } from '../hooks/useSession.tsx';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Canonical tenant navigation — the single source for the sidebar, the
 * mobile drawer, the ⌘K palette, and the header title. Keeping all four
 * on one resolver is what guarantees they can never disagree about
 * "where am I".
 */
export interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  /** Ligature name resolved by ui/Icon to a lucide glyph. */
  readonly icon: string;
  /**
   * Permission gate: visible when the session holds ANY of these.
   * Omitted = no permission requirement.
   */
  readonly permissions?: readonly TenantOrgPermission[];
  /**
   * Role gate: visible when `orgRole` is one of these. Omitted = every
   * signed-in role. Applied together with `permissions` when both are set.
   */
  readonly roles?: readonly OrgMemberRole[];
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'shell.nav.dashboard', testId: 'dashboard', icon: 'dashboard' },
  {
    to: '/keys',
    labelKey: 'shell.nav.keys',
    testId: 'keys',
    icon: 'vpn_key',
    permissions: API_KEYS_PERMISSIONS,
  },
  {
    to: '/usage',
    labelKey: 'shell.nav.usage',
    testId: 'usage',
    icon: 'analytics',
    permissions: ['member:usage:read', 'org:usage:read'],
  },
  {
    to: '/organization',
    labelKey: 'shell.nav.organization',
    testId: 'organization',
    icon: 'account_tree',
    permissions: ['org:members:read'],
  },
  {
    to: '/requests',
    labelKey: 'shell.nav.requests',
    testId: 'requests',
    icon: 'receipt_long',
    permissions: ['member:requests:read', 'org:usage:read'],
  },
  {
    to: '/optimization',
    labelKey: 'shell.nav.optimization',
    testId: 'optimization',
    icon: 'query_stats',
    permissions: ['member:usage:read', 'org:usage:read'],
  },
  { to: '/pricing', labelKey: 'shell.nav.pricing', testId: 'pricing', icon: 'sell' },
  {
    to: '/billing',
    labelKey: 'shell.nav.billing',
    testId: 'billing',
    icon: 'payments',
    permissions: ['org:billing:read'],
  },
  // Ungated on purpose. `/settings` is the PERSONAL account page —
  // account details, technical ids, and the sign-in password form — and
  // every signed-in member may use all of it. The one org-level section
  // on it, `MembersCard`, checks `org:members:read` itself inside
  // `Settings.tsx`. Gating the nav entry on that permission locked the
  // whole page behind one of its cards, and because the sidebar, the
  // drawer and the ⌘K palette all read `visibleNavItems`, the page went
  // missing from every entrance at once — leaving a member no way to
  // change their password short of guessing the URL.
  { to: '/settings', labelKey: 'shell.nav.settings', testId: 'settings', icon: 'settings' },
  // Reference pages — no permission gate: integration docs and platform
  // availability are useful to every signed-in member, including one with
  // no key, usage or billing rights.
  { to: '/docs', labelKey: 'shell.nav.docs', testId: 'docs', icon: 'article' },
  // Role gate, not a permission: platform-api allows org admin keys only
  // for an active owner or admin (`v1-org-admin-keys.ts`). Finance holds
  // billing permissions and must still not see this entrance.
  {
    to: '/admin-api',
    labelKey: 'shell.nav.adminApi',
    testId: 'admin-api',
    icon: 'terminal',
    roles: ['owner', 'admin'],
  },
  { to: '/status', labelKey: 'shell.nav.status', testId: 'status', icon: 'health_and_safety' },
  // Ungated like the reference pages: `/v1/tickets` is open to every
  // signed-in member by contract (tenant #116). Visibility scoping
  // happens server-side — members see the tickets they created,
  // owner/admin the whole org — so there is no org permission to gate
  // the entrance on.
  { to: '/tickets', labelKey: 'shell.nav.tickets', testId: 'tickets', icon: 'support_agent' },
];

/**
 * One predicate for the sidebar, the drawer, the ⌘K palette, and any
 * page that must refuse the same people who do not see the nav row.
 */
export function isNavItemVisible(user: SessionUser | null, item: NavItem): boolean {
  const roleOk = item.roles === undefined || (user !== null && item.roles.includes(user.orgRole));
  const permissionOk = item.permissions === undefined || hasAnyPermission(user, item.permissions);
  return roleOk && permissionOk;
}

export function visibleNavItems(user: SessionUser | null): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => isNavItemVisible(user, item));
}

/**
 * Resolve the nav item that owns a pathname, or null when none does.
 *
 * Matching is SEGMENT-AWARE and longest-first. A bare
 * `pathname.startsWith(item.to)` — the shape this replaced — matched
 * `/keysfoo` against `/keys`, so a 404 rendered the right body under a
 * "Keys" header with the Keys row highlighted. Requiring either an
 * exact hit or a `/` boundary kills that, and sorting by descending
 * path length means a nested target always beats its ancestor, so
 * exactly ONE row can ever light up.
 */
export function activeNavItemForPath(user: SessionUser | null, pathname: string): NavItem | null {
  return (
    [...visibleNavItems(user)]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)) ?? null
  );
}
